/**
 * 2PL IRT + discrete EAP (normal prior) utilities for multistage-style scoring.
 * P(correct | θ,a,b) = 1 / (1 + exp(−1.7·a·(θ − b))); b = rawDifficulty − 1.5.
 */

export const IRT_D = 1.7;

export const MST_C_LOW = -0.5;
export const MST_C_HIGH = 0.5;

const THETA_GRID_POINTS = 41;
const THETA_MIN = -4;
const THETA_MAX = 4;

export function difficultyToB(rawDifficulty, offset = 1.5) {
  const d = parseFloat(rawDifficulty);
  if (Number.isNaN(d)) return 0;
  return d - offset;
}

export function twoPlP(theta, a, b) {
  const aa = Number.isFinite(a) && a > 0 ? a : 1;
  const z = IRT_D * aa * (theta - b);
  if (z > 35) return 1 - 1e-15;
  if (z < -35) return 1e-15;
  return 1 / (1 + Math.exp(-z));
}

export function buildThetaGrid(
  numPoints = THETA_GRID_POINTS,
  thetaMin = THETA_MIN,
  thetaMax = THETA_MAX
) {
  const out = [];
  const n = Math.max(1, numPoints - 1);
  for (let i = 0; i < numPoints; i++) {
    out.push(thetaMin + (i / n) * (thetaMax - thetaMin));
  }
  return out;
}

/** Unnormalized N(0,1) density (proportional to prior mass at grid nodes). */
function normalPdf(t) {
  return Math.exp(-0.5 * t * t);
}

export function normalizedPriorOnGrid(thetas) {
  const w = thetas.map((t) => normalPdf(t));
  const s = w.reduce((acc, x) => acc + x, 0);
  if (s <= 0) return thetas.map(() => 1 / thetas.length);
  return w.map((x) => x / s);
}

export function logLikelihoodAtTheta(theta, responses) {
  let sum = 0;
  for (const { u, a, b } of responses) {
    const p = twoPlP(theta, a, b);
    const pc = Math.min(1 - 1e-12, Math.max(1e-12, p));
    const uu = u ? 1 : 0;
    sum += uu * Math.log(pc) + (1 - uu) * Math.log(1 - pc);
  }
  return sum;
}

/**
 * EAP θ̂ and posterior SD on a fixed θ-grid with N(0,1) prior weights.
 * @param {Array<{ u: 0|1, a: number, b: number }>} responses
 */
export function eapEstimateSE(responses) {
  if (!responses || responses.length === 0) {
    return { theta: 0, se: 1 };
  }

  const thetas = buildThetaGrid();
  const pi = normalizedPriorOnGrid(thetas);

  let maxLog = -Infinity;
  const logW = thetas.map((t, i) => {
    const ll = logLikelihoodAtTheta(t, responses);
    const logPi = Math.log(pi[i] + 1e-300);
    const v = ll + logPi;
    if (v > maxLog) maxLog = v;
    return v;
  });

  const w = logW.map((v) => Math.exp(v - maxLog));
  const sumW = w.reduce((acc, x) => acc + x, 0);
  if (sumW <= 0 || !Number.isFinite(sumW)) {
    return { theta: 0, se: 1 };
  }

  const post = w.map((x) => x / sumW);
  let thetaHat = 0;
  let eTheta2 = 0;
  for (let i = 0; i < thetas.length; i++) {
    thetaHat += thetas[i] * post[i];
    eTheta2 += thetas[i] * thetas[i] * post[i];
  }
  const varPost = Math.max(0, eTheta2 - thetaHat * thetaHat);
  const se = Math.sqrt(varPost);
  return { theta: thetaHat, se };
}

/** Map θ on roughly [−3, +3] to [scaleMin, scaleMax] (linear, clamped). */
export function thetaToScaleScore(theta, scaleMin = 0, scaleMax = 100) {
  const A = (scaleMax - scaleMin) / 6;
  const B = (scaleMin + scaleMax) / 2;
  const raw = A * theta + B;
  return Math.round(Math.max(scaleMin, Math.min(scaleMax, raw)));
}

export function routeFromTheta(thetaHat, cLow = MST_C_LOW, cHigh = MST_C_HIGH) {
  if (thetaHat < cLow) return 'easier';
  if (thetaHat > cHigh) return 'harder';
  return 'medium';
}

export function buildIrtResponses(assignment, correctQuestions, incorrectQuestions) {
  const qs = assignment?.questions || [];
  const findQ = (text) => qs.find((q) => q.question === text);
  const list = [];

  for (const c of correctQuestions || []) {
    const q = findQ(c.question);
    if (!q) continue;
    const a = parseFloat(q.discrimination);
    list.push({
      u: 1,
      a: Number.isFinite(a) && a > 0 ? a : 1,
      b: difficultyToB(q.difficultyScore),
    });
  }
  for (const inc of incorrectQuestions || []) {
    const q = findQ(inc.question);
    if (!q) continue;
    const a = parseFloat(q.discrimination);
    list.push({
      u: 0,
      a: Number.isFinite(a) && a > 0 ? a : 1,
      b: difficultyToB(q.difficultyScore),
    });
  }
  return list;
}

/**
 * @returns {{ squareScore: number, theta: number, se: number, routeHint: string }}
 */
export function irtScoreBundle(assignment, correctQuestions, incorrectQuestions, scaleMin = 0, scaleMax = 100) {
  const responses = buildIrtResponses(assignment, correctQuestions, incorrectQuestions);
  if (responses.length === 0) {
    return { squareScore: 0, theta: 0, se: 1, routeHint: 'medium' };
  }
  const { theta, se } = eapEstimateSE(responses);
  return {
    squareScore: thetaToScaleScore(theta, scaleMin, scaleMax),
    theta,
    se,
    routeHint: routeFromTheta(theta),
  };
}

/** Fisher information at θ for one item: I = a² P(1−P). */
export function fisherInformationItem(theta, a, b) {
  const aa = Number.isFinite(a) && a > 0 ? a : 1;
  const p = twoPlP(theta, aa, b);
  return aa * aa * p * (1 - p);
}

export function meanFisherAtTheta(theta, items) {
  if (!items || !items.length) return 0;
  let s = 0;
  for (const it of items) {
    s += fisherInformationItem(theta, it.a ?? 1, it.b ?? 0);
  }
  return s / items.length;
}
